import React, { useMemo, useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { Plus, User, Clock, MapPin, AlertCircle, Shield, Globe, Tag, FileText, Box, Truck, Activity, ListChecks, Building2 } from 'lucide-react';

import FilterChip from './FilterChip';

/**
 * Per-tab default chip sets for the reporting filter bar.
 * A default chip is always visible; optional chips are revealed via `+ Add Filter`.
 */
const DEFAULT_KEYS = {
  workOrders:       ['assignedTo', 'dueDate', 'location', 'priority'],
  assetHealth:      ['criticality', 'assetCategory', 'location'],
  reportingDetails: ['assignedTo', 'dueDate', 'location', 'priority'],
  recentActivity:   ['assignedTo', 'dueDate', 'location', 'priority'],
};

/**
 * Per-tab pool of filters available via the `+ Add Filter` button.
 * Includes the defaults as well as the optional additions; the popover shows
 * whatever isn't already visible on the bar.
 * Asset Health intentionally excludes WO-specific filters (assignedTo, dueDate,
 * priority, status, workOrderCategory) and the single-asset filter — those
 * don't make sense against aggregate asset-reliability reports.
 */
const POOL_KEYS = {
  workOrders: [
    'assignedTo', 'dueDate', 'location', 'priority',
    'assetCategory', 'asset', 'criticality',
    'workOrderCategory', 'vendor', 'contract', 'status',
  ],
  assetHealth: [
    'criticality', 'assetCategory', 'location',
    'vendor', 'contract',
  ],
  reportingDetails: [
    'assignedTo', 'dueDate', 'location', 'priority',
    'assetCategory', 'asset', 'criticality',
    'workOrderCategory', 'vendor', 'contract', 'status',
  ],
  recentActivity: [
    'assignedTo', 'dueDate', 'location', 'priority',
    'assetCategory', 'asset', 'criticality',
    'workOrderCategory', 'vendor', 'contract', 'status',
  ],
};

const FILTER_ICONS = {
  organization: Building2,
  assignedTo: User,
  dueDate: Clock,
  location: MapPin,
  priority: AlertCircle,
  assetCategory: Box,
  asset: Activity,
  criticality: Shield,
  workOrderCategory: Tag,
  vendor: Globe,
  contract: FileText,
  status: ListChecks,
};

const ReportingFilterBar = ({
  tabKey,
  filters,
  onChange,
  visibleExtras,
  onAddFilter,
  onRemoveFilter,
  // Reference data
  technicianOptions = [],
  locationOptions = [],
  vendorOptions = [],
  assetTypeOptions = [],
  assetOptions = [],
  criticalityOptions = [],
  priorityOptions = [],
  statusOptions = [],
  workOrderCategoryOptions = [],
  contractOptions = [],
  refDataLoading = false,
  // Optional Organization (sub-org) multi-select. When `organizationOptions`
  // is provided (Global Reporting only), the bar renders an Organization
  // chip as the first filter. Per-org Reporting omits these props and the
  // chip stays hidden.
  organizationOptions = null,
  selectedOrganizationIds = [],
  onToggleOrganization,
  onClearOrganizations,
}) => {
  const { t } = useTranslation();

  const hasOrgFilter = Array.isArray(organizationOptions);
  const defaultKeys = DEFAULT_KEYS[tabKey] || [];
  const visibleKeys = useMemo(() => {
    // Organization chip (when enabled) is pinned first, then the tab's
    // default chips, then any extras the user added via + Add Filter.
    const order = [];
    if (hasOrgFilter) order.push('organization');
    defaultKeys.forEach(k => { if (!order.includes(k)) order.push(k); });
    (visibleExtras || []).forEach(k => { if (!order.includes(k)) order.push(k); });
    return order;
  }, [hasOrgFilter, defaultKeys, visibleExtras]);

  const addableKeys = useMemo(
    () => (POOL_KEYS[tabKey] || []).filter(k => !visibleKeys.includes(k)),
    [tabKey, visibleKeys],
  );

  const FILTER_LABELS = useMemo(() => ({
    organization: t('shared.global_org.organization', 'Organization'),
    assignedTo: t('shared.assigned_to', 'Assigned To'),
    dueDate: t('shared.due_date', 'Due Date'),
    location: t('shared.location', 'Location'),
    priority: t('shared.priority', 'Priority'),
    assetCategory: t('shared.asset_category', 'Asset Category'),
    asset: t('shared.asset', 'Asset'),
    criticality: t('shared.criticality', 'Criticality'),
    workOrderCategory: t('shared.work_order_category', 'Work Order Category'),
    vendor: t('shared.vendor', 'Vendor'),
    contract: t('shared.contract', 'Contract'),
    status: t('shared.status', 'Status'),
  }), [t]);

  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (addRef.current && !addRef.current.contains(e.target)) setAddOpen(false);
    };
    if (addOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addOpen]);

  const renderChipBody = (key) => {
    switch (key) {
      case 'organization':
        return (close) => (
          <div style={{ minWidth: 220 }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong className="font-size-12 text-muted text-uppercase">
                {FILTER_LABELS.organization}
              </strong>
              {selectedOrganizationIds.length > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  onClick={() => { onClearOrganizations?.(); close(); }}
                >
                  {t('shared.clear', 'Clear')}
                </button>
              )}
            </div>
            {(organizationOptions || []).map((org) => (
              <label
                key={org.id}
                className="d-flex align-items-center gap-2 py-1"
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={selectedOrganizationIds.includes(org.id)}
                  onChange={() => onToggleOrganization?.(org.id)}
                />
                <span>{org.name}</span>
              </label>
            ))}
            {(organizationOptions || []).length === 0 && (
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>—</div>
            )}
          </div>
        );
      case 'assignedTo': {
        const selected = technicianOptions.find(o => o.value === filters.assignedTo) || null;
        return (close) => (
          <Select
            options={technicianOptions}
            value={selected}
            onChange={(opt) => { onChange({ assignedTo: opt ? opt.value : null }); close(); }}
            isClearable
            placeholder={t('shared.select_technician', 'Select technician...')}
            menuIsOpen
            isLoading={refDataLoading}
            styles={{
              control: (base) => ({ ...base, minWidth: '220px' }),
              menu: (base) => ({ ...base, position: 'relative', boxShadow: 'none', border: 'none' }),
            }}
          />
        );
      }
      case 'dueDate':
        return (close) => (
          <div className="d-flex flex-column gap-2" style={{ minWidth: '220px' }}>
            <label className="text-muted" style={{ fontSize: '0.75rem' }}>{t('shared.from', 'From')}</label>
            <DatePicker
              selected={filters.dueDateStart ? new Date(filters.dueDateStart) : null}
              onChange={(date) => onChange({ dueDateStart: date ? `${date.toISOString().split('T')[0]}T00:00:00.000` : null })}
              dateFormat="dd/MM/yyyy"
              className="form-control form-control-sm"
              placeholderText={t('shared.select_date', 'Select date')}
              isClearable
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
            <label className="text-muted" style={{ fontSize: '0.75rem' }}>{t('shared.to', 'To')}</label>
            <DatePicker
              selected={filters.dueDateEnd ? new Date(filters.dueDateEnd) : null}
              onChange={(date) => onChange({ dueDateEnd: date ? `${date.toISOString().split('T')[0]}T23:59:59.999` : null })}
              dateFormat="dd/MM/yyyy"
              className="form-control form-control-sm"
              placeholderText={t('shared.select_date', 'Select date')}
              isClearable
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
            <button className="btn btn-sm btn-primary mt-1" onClick={close}>
              {t('shared.apply', 'Apply')}
            </button>
          </div>
        );
      case 'location': {
        const selected = locationOptions.find(o => o.value === filters.locationId) || null;
        return (close) => (
          <Select
            options={locationOptions}
            value={selected}
            onChange={(opt) => { onChange({ locationId: opt ? opt.value : null }); close(); }}
            isClearable
            placeholder={t('shared.select_location', 'Select location...')}
            menuIsOpen
            isLoading={refDataLoading}
            styles={{
              control: (base) => ({ ...base, minWidth: '220px' }),
              menu: (base) => ({ ...base, position: 'relative', boxShadow: 'none', border: 'none' }),
            }}
          />
        );
      }
      case 'priority':
        return (close) => (
          <div className="d-flex flex-column gap-1" style={{ minWidth: '140px' }}>
            {priorityOptions.map(opt => (
              <button
                key={opt.value}
                className={`btn btn-sm text-start ${filters.priority === opt.value ? 'btn-primary' : 'btn-light'}`}
                onClick={() => { onChange({ priority: opt.value }); close(); }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        );
      case 'assetCategory': {
        const selected = assetTypeOptions.find(o => o.value === filters.assetTypeId) || null;
        return (close) => (
          <Select
            options={assetTypeOptions}
            value={selected}
            onChange={(opt) => { onChange({ assetTypeId: opt ? opt.value : null }); close(); }}
            isClearable
            placeholder={t('shared.select_asset_category', 'Select asset category...')}
            menuIsOpen
            styles={{
              control: (base) => ({ ...base, minWidth: '220px' }),
              menu: (base) => ({ ...base, position: 'relative', boxShadow: 'none', border: 'none' }),
            }}
          />
        );
      }
      case 'asset': {
        const selected = assetOptions.find(o => o.value === filters.assetId) || null;
        return (close) => (
          <Select
            options={assetOptions}
            value={selected}
            onChange={(opt) => { onChange({ assetId: opt ? opt.value : null }); close(); }}
            isClearable
            placeholder={t('shared.select_asset', 'Select asset...')}
            menuIsOpen
            styles={{
              control: (base) => ({ ...base, minWidth: '220px' }),
              menu: (base) => ({ ...base, position: 'relative', boxShadow: 'none', border: 'none' }),
            }}
          />
        );
      }
      case 'criticality':
        return (close) => (
          <div className="d-flex flex-column gap-1" style={{ minWidth: '160px' }}>
            {criticalityOptions.map(opt => (
              <button
                key={opt.value}
                className={`btn btn-sm text-start text-uppercase ${filters.criticality === opt.value ? 'btn-primary' : 'btn-light'}`}
                onClick={() => { onChange({ criticality: opt.value }); close(); }}
              >
                {t(`shared.${opt.value?.toLowerCase()}`, opt.value)}
              </button>
            ))}
            {criticalityOptions.length === 0 && (
              <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                {t('shared.no_options_available', 'No options available')}
              </span>
            )}
          </div>
        );
      case 'workOrderCategory': {
        const selected = workOrderCategoryOptions.find(o => o.value === filters.workOrderCategoryId) || null;
        return (close) => (
          <Select
            options={workOrderCategoryOptions}
            value={selected}
            onChange={(opt) => { onChange({ workOrderCategoryId: opt ? opt.value : null }); close(); }}
            isClearable
            placeholder={t('shared.select_work_order_category', 'Select category...')}
            menuIsOpen
            styles={{
              control: (base) => ({ ...base, minWidth: '220px' }),
              menu: (base) => ({ ...base, position: 'relative', boxShadow: 'none', border: 'none' }),
            }}
          />
        );
      }
      case 'vendor': {
        const selected = vendorOptions.find(o => o.value === filters.vendorId) || null;
        return (close) => (
          <Select
            options={vendorOptions}
            value={selected}
            onChange={(opt) => { onChange({ vendorId: opt ? opt.value : null }); close(); }}
            isClearable
            placeholder={t('shared.select_vendor', 'Select vendor...')}
            menuIsOpen
            styles={{
              control: (base) => ({ ...base, minWidth: '220px' }),
              menu: (base) => ({ ...base, position: 'relative', boxShadow: 'none', border: 'none' }),
            }}
          />
        );
      }
      case 'contract': {
        const selected = contractOptions.find(o => o.value === filters.contractNumber) || null;
        return (close) => (
          <Select
            options={contractOptions}
            value={selected}
            onChange={(opt) => { onChange({ contractNumber: opt ? opt.value : null }); close(); }}
            isClearable
            placeholder={t('shared.select_contract', 'Select contract...')}
            menuIsOpen
            styles={{
              control: (base) => ({ ...base, minWidth: '260px' }),
              menu: (base) => ({ ...base, position: 'relative', boxShadow: 'none', border: 'none' }),
            }}
          />
        );
      }
      case 'status': {
        const selected = statusOptions.find(o => o.value === filters.status) || null;
        return (close) => (
          <Select
            options={statusOptions}
            value={selected}
            onChange={(opt) => { onChange({ status: opt ? opt.value : null }); close(); }}
            isClearable
            placeholder={t('shared.select_status', 'Select status...')}
            menuIsOpen
            styles={{
              control: (base) => ({ ...base, minWidth: '220px' }),
              menu: (base) => ({ ...base, position: 'relative', boxShadow: 'none', border: 'none' }),
            }}
          />
        );
      }
      default:
        return null;
    }
  };

  const isActive = (key) => {
    switch (key) {
      case 'organization': return selectedOrganizationIds.length > 0;
      case 'assignedTo': return filters.assignedTo != null;
      case 'dueDate': return filters.dueDateStart != null || filters.dueDateEnd != null;
      case 'location': return filters.locationId != null;
      case 'priority': return filters.priority != null;
      case 'assetCategory': return filters.assetTypeId != null;
      case 'asset': return filters.assetId != null;
      case 'criticality': return filters.criticality != null;
      case 'workOrderCategory': return filters.workOrderCategoryId != null;
      case 'vendor': return filters.vendorId != null;
      case 'contract': return filters.contractNumber != null;
      case 'status': return filters.status != null;
      default: return false;
    }
  };

  const displayValueFor = (key) => {
    switch (key) {
      case 'organization': {
        if (selectedOrganizationIds.length === 0) return null;
        if (selectedOrganizationIds.length === 1) {
          return (organizationOptions || []).find(o => o.id === selectedOrganizationIds[0])?.name
            || `1 ${t('shared.selected', 'selected')}`;
        }
        return `${selectedOrganizationIds.length} ${t('shared.selected', 'selected')}`;
      }
      case 'assignedTo': return technicianOptions.find(o => o.value === filters.assignedTo)?.label;
      case 'dueDate': return filters.dueDateStart ? t('shared.due_date_set', 'Due date set') : null;
      case 'location': return locationOptions.find(o => o.value === filters.locationId)?.label;
      case 'priority': return priorityOptions.find(o => o.value === filters.priority)?.label;
      case 'assetCategory': return assetTypeOptions.find(o => o.value === filters.assetTypeId)?.label;
      case 'asset': return assetOptions.find(o => o.value === filters.assetId)?.label;
      case 'criticality': return filters.criticality && t(`shared.${filters.criticality.toLowerCase()}`, filters.criticality);
      case 'workOrderCategory': return workOrderCategoryOptions.find(o => o.value === filters.workOrderCategoryId)?.label;
      case 'vendor': return vendorOptions.find(o => o.value === filters.vendorId)?.label;
      case 'contract': return contractOptions.find(o => o.value === filters.contractNumber)?.label;
      case 'status': return statusOptions.find(o => o.value === filters.status)?.label;
      default: return null;
    }
  };

  const clearValueFor = (key) => {
    switch (key) {
      case 'organization': onClearOrganizations?.(); break;
      case 'assignedTo': onChange({ assignedTo: null }); break;
      case 'dueDate': onChange({ dueDateStart: null, dueDateEnd: null }); break;
      case 'location': onChange({ locationId: null }); break;
      case 'priority': onChange({ priority: null }); break;
      case 'assetCategory': onChange({ assetTypeId: null }); break;
      case 'asset': onChange({ assetId: null }); break;
      case 'criticality': onChange({ criticality: null }); break;
      case 'workOrderCategory': onChange({ workOrderCategoryId: null }); break;
      case 'vendor': onChange({ vendorId: null }); break;
      case 'contract': onChange({ contractNumber: null }); break;
      case 'status': onChange({ status: null }); break;
      default: break;
    }
  };

  const handleClear = (key) => {
    clearValueFor(key);
    // Also remove extra chips (optional ones) from the bar when cleared.
    if (!defaultKeys.includes(key) && onRemoveFilter) {
      onRemoveFilter(key);
    }
  };

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      {visibleKeys.map(key => (
        <FilterChip
          key={key}
          icon={FILTER_ICONS[key]}
          label={FILTER_LABELS[key]}
          value={isActive(key) ? true : null}
          displayValue={displayValueFor(key) || FILTER_LABELS[key]}
          onClear={() => handleClear(key)}
        >
          {renderChipBody(key)}
        </FilterChip>
      ))}

      {addableKeys.length > 0 && (
        <div ref={addRef} style={{ position: 'relative' }}>
          <motion.button
            className="btn btn-sm d-flex align-items-center gap-1"
            style={{
              border: '1px dashed #adb5bd',
              backgroundColor: '#fff',
              borderRadius: '20px',
              padding: '4px 12px',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: 'var(--bs-primary)',
              whiteSpace: 'nowrap',
            }}
            onClick={() => setAddOpen(!addOpen)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus size={14} />
            <span>{t('shared.add_filter', 'Add Filter')}</span>
          </motion.button>

          <AnimatePresence>
            {addOpen && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="bg-white border rounded shadow-sm"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '4px',
                  zIndex: 1050,
                  minWidth: '200px',
                  padding: '6px',
                }}
              >
                <div className="d-flex flex-column gap-1">
                  {addableKeys.map(k => {
                    const Icon = FILTER_ICONS[k];
                    return (
                      <button
                        key={k}
                        className="btn btn-sm btn-light text-start d-flex align-items-center gap-2"
                        onClick={() => {
                          onAddFilter?.(k);
                          setAddOpen(false);
                        }}
                      >
                        {Icon && <Icon size={14} />}
                        <span>{FILTER_LABELS[k]}</span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default ReportingFilterBar;
